import { ReactNode, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { DataTable } from '@/components/ui/data-table'
import { ColumnDef } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import {
  useGuildMembers,
  useTeamTrackingRules,
} from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useGuild } from 'app/frontend/base/config/context/guild'
import useBasePath from 'app/frontend/base/hooks/useBasePath'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'
import SearchBar from 'app/frontend/base/components/SearchBar'
import { useQueryClient } from '@tanstack/react-query'
import {
  name,
  characterClass,
  level,
  rankInGuild,
} from 'app/frontend/base/config/column-definitions/character'
import {
  getSpecializationsByClass,
  getRolesWithMultipleSpecs,
} from 'app/frontend/base/services/characterValues'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { CheckIcon, Loader2, Trash2 } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import TrackingRules, {
  EditableTrackingRule,
  extendTrackingRules,
} from './TrackingRules'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Link } from 'react-router-dom'
import { PREMIUM_BENEFIT_CONFIG } from 'app/frontend/types/custom/PremiumBenefitCategory'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { CharacterRole } from 'app/frontend/types/custom'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import { z } from 'zod'
import Card from 'app/frontend/base/components/Card'
import { useBreakpoint } from 'app/frontend/base/hooks/useBreakpoint'
import Select from 'app/frontend/base/components/Select'
import GuildDataRefreshButton from 'app/frontend/base/components/GuildDataRefreshButton'

export interface AddCharacterModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  mode?: 'roster' | 'event'
  title?: string
  defaultTab?: 'guild' | 'search' | 'tracking-rules' | 'roster'
  existingCharacterIds?: Set<number>
  existingUserIds?: Set<number>
  onAddCharacter?: (params: {
    characterId: number
    specId?: number
    role?: string
    teamRankId?: number
  }) => void
  isAddingAny?: boolean
  addingCharacterId?: number
  onRemoveCharacter?: (memberId: number) => void
  isRemovingAny?: boolean
  removingMemberId?: number
  onOpenChange?: (open: boolean) => void
  hideTrackingRulesTab?: boolean
  guildOnly?: boolean
  introContent?: ReactNode
  simpleAdd?: boolean
  addLabel?: string
}

const formSchema = z.object({
  tracking_rules_attributes: z.array(
    z.object({
      id: z.number().optional(),
      rank: z.number(),
      teamRankId: z.number().nullable().optional(),
      _destroy: z.boolean().optional(),
    }),
  ),
})

const manualTeamRankStorageKeyPrefix = 'team-roster-manual-team-rank-id'

const AddCharacterModal = ({
  open,
  setOpen,
  mode = 'roster',
  title,
  defaultTab,
  existingCharacterIds,
  existingUserIds,
  onAddCharacter,
  isAddingAny,
  addingCharacterId,
  onRemoveCharacter,
  isRemovingAny,
  removingMemberId,
  onOpenChange,
  hideTrackingRulesTab,
  guildOnly,
  introContent,
  simpleAdd,
  addLabel,
}: AddCharacterModalProps) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const { guildReference, guild } = useGuild()
  const { customDomain } = useCustomDomain()
  const baseGuildPath = useBasePath('guild')
  const queryClient = useQueryClient()
  const breakpoint = useBreakpoint()
  const guildId = open ? guildReference?.id : undefined
  const trackingRulesTeamId = open ? team.id : undefined
  const manualTeamRankStorageKey = `${manualTeamRankStorageKeyPrefix}-${team.id}`

  const { data: guildMembers, isPending } = useGuildMembers(guildId)
  const { data: trackingRules, isPending: loadingTrackingRules } =
    useTeamTrackingRules(trackingRulesTeamId)

  const refreshGuildData = () => {
    queryClient.invalidateQueries({
      queryKey: ['guild', { path: customDomain?.guild ? '' : baseGuildPath }],
    })
  }

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open)
    setOpen(open)
  }

  const readonly =
    mode === 'roster' && guild?.characterLimit && team.members?.length
      ? team.members.length >= guild.characterLimit
      : false

  const atMaxLimit =
    guild?.characterLimit === PREMIUM_BENEFIT_CONFIG.character_limit.max

  const isAddingMember = (characterId: number) => {
    return !!isAddingAny && addingCharacterId === characterId
  }

  const isRemovingMember = (memberId: number) => {
    return !!isRemovingAny && removingMemberId === memberId
  }

  const teamRankOptions = useMemo(() => {
    return (
      team.teamRanks?.map((rank) => ({
        id: rank.id,
        label: rank.name || `Rank ${rank.id}`,
        default: rank.default,
      })) ?? []
    )
  }, [team.teamRanks])
  const manualTeamRankSelectOptions = useMemo(() => {
    return teamRankOptions.map((rank) => ({
      value: rank.id.toString(),
      label: rank.label,
    }))
  }, [teamRankOptions])

  const [manualTeamRankId, setManualTeamRankId] = useState<number | undefined>(
    () => {
      if (typeof window === 'undefined') return undefined
      const storedValue = window.sessionStorage.getItem(
        manualTeamRankStorageKey,
      )
      return storedValue ? Number(storedValue) : undefined
    },
  )

  const selectedManualTeamRankId = useMemo(() => {
    if (teamRankOptions.some((option) => option.id === manualTeamRankId)) {
      return manualTeamRankId
    }

    if (typeof window !== 'undefined') {
      const storedValue = window.sessionStorage.getItem(
        manualTeamRankStorageKey,
      )
      const storedTeamRankId = storedValue ? Number(storedValue) : undefined
      if (teamRankOptions.some((option) => option.id === storedTeamRankId)) {
        return storedTeamRankId
      }
    }

    const defaultTeamRankId = teamRankOptions.find(
      (option) => option.default,
    )?.id
    return defaultTeamRankId ?? teamRankOptions[0]?.id
  }, [manualTeamRankId, manualTeamRankStorageKey, teamRankOptions])

  const teamMembers = team.members ?? []
  const membersByCharacterId = useMemo(() => {
    return new Map(
      teamMembers.map((member) => [member.characterReference.id, member]),
    )
  }, [teamMembers])

  const existingIds =
    existingCharacterIds ??
    new Set(teamMembers.map((member) => member.characterReference.id))

  const rosterTabCharacters = useMemo(
    () => teamMembers.map((member) => member.characterReference),
    [teamMembers],
  )

  const addToRosterActions = (character: wowaudit.CharacterReference) => {
    const existingMember = membersByCharacterId.get(character.id)

    const isAlreadyMember = existingIds.has(character.id)
    const isAddingThisMember = isAddingMember(character.id)
    const isRemovingThisMember = existingMember?.id
      ? isRemovingMember(existingMember.id)
      : false
    const groupedWithMain =
      mode === 'event' &&
      (existingMember?.teamRank.player_type === 'alt' ||
        existingUserIds?.has(character.userId ?? 0))

    const classSpecializations = getSpecializationsByClass(character.classId)
    const rolesWithMultipleSpecs = getRolesWithMultipleSpecs(character.classId)

    if (!isAlreadyMember && !groupedWithMain && simpleAdd) {
      return (
        <Button
          size='xs'
          variant='outline'
          disabled={!!isAddingAny || readonly}
          onClick={() =>
            onAddCharacter?.({
              characterId: character.id,
            })
          }
          className='w-full'
        >
          {addLabel ?? 'Add'}
        </Button>
      )
    }

    return (
      <Card
        className={`flex gap-1 items-center px-2 py-1 my-[-0.3rem] w-[13.3rem] h-9 ${
          isAlreadyMember ? 'text-success-foreground' : ''
        }`}
      >
        {isAddingThisMember || isRemovingThisMember ? (
          <div className='flex justify-center items-center w-full h-full'>
            <Loader2 size={16} className='animate-spin text-foreground' />
          </div>
        ) : isAlreadyMember ? (
          <span className='flex items-center gap-2 w-full'>
            <CheckIcon size={16} />
            <span className='grow'>
              {simpleAdd
                ? 'Added'
                : mode === 'roster'
                ? t('dashboard.team.roster.character-already-in-team')
                : 'Participating'}
            </span>
            {onRemoveCharacter && existingMember?.id && (
              <Button
                size='sm'
                variant='outline'
                disabled={!!isRemovingAny}
                onClick={() => onRemoveCharacter(existingMember.id)}
                className='p-1 h-8 w-8 min-w-8 min-h-8 text-destructive-foreground'
              >
                <Trash2 size={16} />
              </Button>
            )}
          </span>
        ) : groupedWithMain ? (
          <span className='text-xs text-muted-foreground'>
            Alts are grouped with mains
          </span>
        ) : (
          <>
            <span className='whitespace-nowrap mr-2'>Add as</span>
            {rolesWithMultipleSpecs?.map((role) => (
              <Tooltip key={`role-${role}`}>
                <TooltipTrigger asChild>
                  <Button
                    size='sm'
                    variant='outline'
                    disabled={!!isAddingAny || readonly}
                    onClick={() =>
                      onAddCharacter?.({
                        characterId: character.id,
                        role: role,
                        teamRankId:
                          mode === 'roster'
                            ? selectedManualTeamRankId
                            : undefined,
                      })
                    }
                    className='p-1 h-8 w-8 min-w-8 min-h-8'
                  >
                    <CharacterRoleIcon
                      role={role as CharacterRole}
                      className='w-6 h-6 border-none min-w-6 min-h-6'
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className='capitalize'>{role}</p>
                </TooltipContent>
              </Tooltip>
            ))}

            {Object.entries(classSpecializations).map(([specId, spec]) => (
              <Tooltip key={specId}>
                <TooltipTrigger asChild>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() =>
                      onAddCharacter?.({
                        characterId: character.id,
                        specId: parseInt(specId),
                        role: spec.default_role,
                        teamRankId:
                          mode === 'roster'
                            ? selectedManualTeamRankId
                            : undefined,
                      })
                    }
                    disabled={!!isAddingAny || readonly}
                    className='p-1 h-8 w-8 min-w-8 min-h-8'
                  >
                    <BlizzardIcon
                      iconName={spec.icon}
                      size='medium'
                      className='border-none min-w-6 min-h-6'
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{spec.name}</p>
                  <p className='text-xs text-muted-foreground capitalize'>
                    {spec.default_role}
                  </p>
                </TooltipContent>
              </Tooltip>
            ))}
          </>
        )}
      </Card>
    )
  }

  const columns: ColumnDef<wowaudit.CharacterReference>[] = useMemo(
    () => [
      name,
      characterClass,
      level,
      rankInGuild(guild?.guildRanks),
      {
        id: 'add',
        cell: ({ row }) => addToRosterActions(row.original),
        enableSorting: false,
        enableHiding: false,
        size: 230,
      },
    ],
    [
      team.members,
      isAddingAny,
      isRemovingAny,
      mode,
      existingCharacterIds,
      membersByCharacterId,
      selectedManualTeamRankId,
      guild?.guildRanks,
    ],
  )

  const manualRankSelector =
    mode !== 'roster' || simpleAdd ? null : (
      <div className='text-xs text-muted-foreground flex justify-end mb-2 gap-1'>
        Members will be added with the{' '}
        {teamRankOptions.length ? (
          <Select
            value={selectedManualTeamRankId?.toString()}
            setValue={(value) => {
              const nextTeamRankId = value ? Number(value) : undefined
              setManualTeamRankId(nextTeamRankId)
              if (nextTeamRankId === undefined) return
              window.sessionStorage.setItem(
                manualTeamRankStorageKey,
                String(nextTeamRankId),
              )
            }}
            options={manualTeamRankSelectOptions}
            hideIcon
            className='inline-flex text-xs text-accent-foreground bg-transparent hover:bg-transparent border-0 w-fit font-normal p-0 h-auto cursor-pointer'
            optionWidth='fit'
          />
        ) : (
          'default'
        )}{' '}
        rank
      </div>
    )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='max-w-200 max-h-[85vh] min-h-[min(47rem,85vh)] overflow-y-auto flex flex-col'>
        <DialogHeader>
          <DialogTitle>
            {title ?? t('dashboard.team.roster.add-members')}
          </DialogTitle>
        </DialogHeader>

        {readonly && mode === 'roster' && (
          <Alert variant={atMaxLimit ? 'info' : 'warning'} className='mb-4'>
            <AlertTitle>
              {t('dashboard.team.character-limit-reached')}
            </AlertTitle>
            <AlertDescription className='flex items-center justify-between gap-2'>
              {t(
                atMaxLimit
                  ? 'dashboard.team.character-limit-reached-description-max'
                  : 'dashboard.team.character-limit-reached-description',
                {
                  limit: guild?.characterLimit,
                },
              )}
              {!atMaxLimit && (
                <Link
                  to={`${team.path}/premium`}
                  onClick={() => setOpen(false)}
                >
                  <Button
                    variant='outline'
                    type='button'
                    size='sm'
                    className='text-foreground'
                  >
                    {t('shared.premium')}
                  </Button>
                </Link>
              )}
            </AlertDescription>
          </Alert>
        )}

        {introContent}

        {guildOnly ? (
          <div className='space-y-4 flex-1'>
            <DataTable
              columns={columns}
              data={guildMembers || []}
              includeHeader={true}
              pagination={10}
              filterType='includesString'
              sortingState={[
                { id: 'rankInGuild', desc: false },
                { id: 'name', desc: false },
              ]}
              emptyMessage={() =>
                isPending
                  ? t('shared.loading')
                  : t('dashboard.team.roster.no-available-members')
              }
              customHeaderActions={
                <GuildDataRefreshButton
                  label='shared.update-list'
                  layout='horizontal'
                  successKey='dashboard.guild.guild-update-success'
                  callback={refreshGuildData}
                  queryToInvalidate={[
                    'guildMembers',
                    { id: guildReference?.id },
                  ]}
                />
              }
            />
          </div>
        ) : (
          <Tabs
            defaultValue={defaultTab ?? (mode === 'event' ? 'roster' : 'guild')}
            className='w-full flex-1 flex flex-col'
          >
            <TabsList
              className={`grid w-full ${
                mode === 'event'
                  ? 'grid-cols-3'
                  : hideTrackingRulesTab
                  ? 'grid-cols-2'
                  : 'grid-cols-3'
              }`}
            >
              {mode === 'event' ? (
                <TabsTrigger value='roster'>
                  From roster{' '}
                  {rosterTabCharacters && breakpoint !== 'xs'
                    ? `(${rosterTabCharacters.length})`
                    : null}
                </TabsTrigger>
              ) : null}
              <TabsTrigger value='guild'>
                {t(
                  breakpoint === 'xs'
                    ? 'shared.guild'
                    : 'dashboard.team.roster.guild-members',
                )}
                {guildMembers && breakpoint !== 'xs'
                  ? ` (${guildMembers?.length})`
                  : null}
              </TabsTrigger>
              <TabsTrigger value='search'>
                {t(
                  breakpoint === 'xs'
                    ? 'shared.manual'
                    : 'dashboard.team.roster.search-characters',
                )}
              </TabsTrigger>
              {mode === 'event' || hideTrackingRulesTab ? null : (
                <TabsTrigger value='tracking-rules'>
                  {t(
                    breakpoint === 'xs'
                      ? 'shared.automatically'
                      : 'dashboard.team.roster.tracking-rules',
                  )}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value='search' className='space-y-4 flex-1'>
              {manualRankSelector}
              <Card className='space-y-2'>
                <SearchBar<wowaudit.CharacterReference>
                  placeholder={t('dashboard.team.roster.search-placeholder')}
                  type='inline'
                  searchType='character'
                  actions={(result) => addToRosterActions(result)}
                  listClassName='max-h-[min(calc(85vh-15rem),520px)] min-h-14'
                  itemClassName='border-b rounded-none first:mt-2'
                />
              </Card>
            </TabsContent>

            {mode === 'event' ? (
              <TabsContent value='roster' className='space-y-4 flex-1'>
                <DataTable
                  columns={columns}
                  data={rosterTabCharacters}
                  includeHeader={true}
                  pagination={10}
                  filterType='includesString'
                  sortingState={[
                    { id: 'rankInGuild', desc: false },
                    { id: 'name', desc: false },
                  ]}
                  emptyMessage={() =>
                    t('dashboard.team.roster.no-available-members')
                  }
                />
              </TabsContent>
            ) : null}

            <TabsContent value='guild' className='space-y-4 flex-1'>
              {manualRankSelector}
              <DataTable
                columns={columns}
                data={guildMembers || []}
                includeHeader={true}
                pagination={10}
                filterType='includesString'
                sortingState={[
                  { id: 'rankInGuild', desc: false },
                  { id: 'name', desc: false },
                ]}
                emptyMessage={() =>
                  isPending
                    ? t('shared.loading')
                    : t('dashboard.team.roster.no-available-members')
                }
                customHeaderActions={
                  <GuildDataRefreshButton
                    label='shared.update-list'
                    layout='horizontal'
                    successKey='dashboard.guild.guild-update-success'
                    callback={refreshGuildData}
                    queryToInvalidate={[
                      'guildMembers',
                      { id: guildReference?.id },
                    ]}
                  />
                }
              />
            </TabsContent>

            {mode === 'event' || hideTrackingRulesTab ? null : (
              <TabsContent value='tracking-rules' className='space-y-4 flex-1'>
                <Card className={`p-0`}>
                  <div className='py-5 px-4'>
                    <p>
                      Members of guild ranks that are enabled here will be added
                      to the roster automatically (once per day).
                    </p>
                  </div>

                  {loadingTrackingRules ? null : (
                    <FormWrapper<
                      wowaudit.Team & {
                        tracking_rules_attributes: EditableTrackingRule[]
                      }
                    >
                      key={team?.id}
                      formSchema={formSchema}
                      entity={{
                        ...team,
                        tracking_rules_attributes: extendTrackingRules(
                          trackingRules ?? [],
                        ),
                      }}
                      onSave={async (form, data) => {
                        const newRules = await queryClient.fetchQuery<
                          wowaudit.TrackingRule[]
                        >({
                          queryKey: ['teamTrackingRules', { id: team?.id }],
                        })

                        form.reset({
                          ...data,
                          tracking_rules_attributes:
                            extendTrackingRules(newRules),
                        })
                      }}
                      transformValues={(values) => {
                        return {
                          ...values,
                          tracking_rules_attributes:
                            values.tracking_rules_attributes.filter(
                              (rule: EditableTrackingRule) =>
                                rule._destroy !== undefined,
                            ),
                        }
                      }}
                      className='mr-4 mb-4'
                      entityType='team'
                    >
                      <TrackingRules />
                    </FormWrapper>
                  )}
                </Card>
              </TabsContent>
            )}
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default AddCharacterModal
